/**
 * Bloc Dashboard "Vous devriez jeter un œil" — alertes en bas de page.
 *
 * Deux sources RÉELLES fusionnées dans le même bloc :
 *   1. Stock bas (`seuilsStock.listerAlertes`) — cuvées passées sous leur seuil
 *      commercialisable (Lot E.2.7). Icône Wine, CTA « Voir le stock ».
 *   2. Matériel (`parcMateriel.listerAlertes`) — entretiens/contrôles à venir,
 *      triés par urgence côté back. Icône Wrench/Tractor, CTA « Voir le matériel ».
 *
 * Le compteur n'affiche un total que sur un vrai décompte chargé (jamais « 0 »
 * pendant un échec réseau : ce serait lu comme « tout est à jour »). Chaque
 * source gère son propre état de chargement / erreur sans masquer l'autre.
 */

import type { AlerteStockBas } from "@champ-erp/contracts";
import { AlertRow, Card, Eyebrow } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Tractor, Wine, Wrench } from "lucide-react";
import { Link } from "react-router-dom";

import { trpc } from "../../lib/trpc.js";
import { formatNbBouteilles } from "../cave/stades.js";
import {
  libelleEcheanceHeures,
  libelleEcheanceJours,
} from "../vignoble/materiel/echeance-format.js";
import { nomMateriel } from "../vignoble/materiel/materiel-types.js";
import { useAlertesMateriel, type AlerteMateriel } from "../vignoble/materiel/use-materiel.js";

export function BlocAlertes() {
  const {
    data: alertesMateriel,
    isLoading: materielLoading,
    isError: materielError,
  } = useAlertesMateriel();
  const {
    data: alertesStock,
    isLoading: stockLoading,
    isError: stockError,
  } = useQuery({
    queryKey: ["seuilsStock.listerAlertes"],
    queryFn: () => trpc.seuilsStock.listerAlertes.query(),
  });

  const materiel = alertesMateriel ?? [];
  const stock = alertesStock ?? [];
  const total = materiel.length + stock.length;

  const enChargement = materielLoading || stockLoading;
  const enErreur = materielError || stockError;

  // Compteur affiché UNIQUEMENT sur un vrai décompte chargé (> 0). En
  // chargement/erreur/vide, on ne montre pas « 0 éléments » (trompeur).
  const afficherCompteur = !enChargement && !enErreur && total > 0;

  return (
    <Card as="section">
      <header className="flex items-baseline justify-between border-b border-champagne-900/10 px-6 pb-4 pt-5">
        <div>
          <Eyebrow>Suivi</Eyebrow>
          <h2 className="mt-1 text-[18px] font-semibold">Vous devriez jeter un œil</h2>
        </div>
        {afficherCompteur && (
          <span className="mono text-[12px] text-champagne-700">
            {total} élément{total > 1 ? "s" : ""}
          </span>
        )}
      </header>

      <div className="px-3 py-2">
        {enChargement ? (
          <p className="px-3 py-3 text-[13px] text-champagne-700">Chargement des alertes…</p>
        ) : (
          <>
            {/* Stock bas (Lot E.2.7) — priorité commerciale (rupture de vente). */}
            {stock.map((alerte) => (
              <StockBasAlertRow key={alerte.seuil_id} alerte={alerte} />
            ))}
            {/* Matériel (déjà trié par urgence côté back). */}
            {materiel.map((alerte) => (
              <MaterielAlertRow key={alerte.plan_id} alerte={alerte} />
            ))}

            {/* Erreurs par source : signalées sans masquer l'autre source. */}
            {stockError && (
              <p className="px-3 py-3 text-[13px] text-champagne-700">
                Impossible de charger les alertes de stock pour l&apos;instant.
              </p>
            )}
            {materielError && (
              <p className="px-3 py-3 text-[13px] text-champagne-700">
                Impossible de charger les alertes matériel pour l&apos;instant.
              </p>
            )}

            {/* Vrai « tout est à jour » : aucune alerte ET aucune erreur. */}
            {total === 0 && !enErreur && (
              <p className="px-3 py-3 text-[13px] text-champagne-700">
                Aucune alerte : stock au-dessus des seuils et entretiens matériel à jour.
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * Rendu d'une alerte de stock bas via `AlertRow`. Icône Wine (distingue « stock »
 * de « matériel »). Titre = phrase d'action ; corps = bouteilles restantes +
 * seuil + ventilation honnête (dont habillées) ; CTA outline vers le stock.
 */
function StockBasAlertRow({ alerte }: { alerte: AlerteStockBas }) {
  const titre = `${alerte.cuvee_nom} · ${alerte.format_libelle} : stock bas.`;
  const corps = `${formatNbBouteilles(alerte.stock_commercialisable)} bt restantes (seuil ${formatNbBouteilles(
    alerte.seuil_bas,
  )}) · dont ${formatNbBouteilles(alerte.ventilation_par_stade.habillee)} habillées.`;

  const action = (
    <Link
      to="/stock"
      className="ui-btn-outline inline-flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[13px]"
    >
      Voir le stock <ArrowRight size={14} />
    </Link>
  );

  return (
    <AlertRow
      type="warn"
      title={titre}
      body={corps}
      meta={<span>Catégorie Stock</span>}
      action={action}
      icon={<Wine size={16} />}
    />
  );
}

/**
 * Rendu d'une alerte matériel via `AlertRow`. Icône surchargée (Wrench /
 * Tractor) pour distinguer « matériel » de « cave ». Titre = phrase d'action ;
 * corps = matériel + échéance (jours et/ou heures) ; meta = Tag « Obligatoire »
 * (si réglementaire) + « Catégorie Matériel » ; CTA outline vers la fiche.
 */
function MaterielAlertRow({ alerte }: { alerte: AlerteMateriel }) {
  const nom = nomMateriel({
    type: alerte.materiel_type,
    designation: alerte.materiel_designation,
    marque: alerte.materiel_marque,
    modele: alerte.materiel_modele,
  });

  // Phrase d'échéance combinée (date et/ou heures), pour le corps de l'alerte.
  const echeanceParts = [
    libelleEcheanceJours(alerte.alerte.joursRestants),
    libelleEcheanceHeures(alerte.alerte.heuresRestantes),
  ].filter((p): p is string => p !== null);
  const echeance = echeanceParts.length > 0 ? echeanceParts.join(" · ") : "échéance atteinte";

  const titre = alerte.reglementaire
    ? `‼ ${alerte.libelle} — contrôle obligatoire.`
    : `${alerte.libelle} à prévoir.`;
  const corps = `${nom} · ${echeance}.`;

  const meta = (
    <>
      {alerte.reglementaire && (
        <span className="inline-flex items-center rounded-full border border-rust-200 bg-rust-50 px-2 py-0.5 text-[11px] font-semibold text-rust-700">
          Obligatoire
        </span>
      )}
      <span>Catégorie Matériel</span>
    </>
  );

  const action = (
    <Link
      to={`/vignoble/materiel/${alerte.materiel_id}`}
      className="ui-btn-outline inline-flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[13px]"
    >
      Voir le matériel <ArrowRight size={14} />
    </Link>
  );

  return (
    <AlertRow
      type="warn"
      title={titre}
      body={corps}
      meta={meta}
      action={action}
      icon={alerte.reglementaire ? <Wrench size={16} /> : <Tractor size={16} />}
    />
  );
}
